import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, LinkSimple, Lifebuoy, Plugs, CheckCircle, ArrowClockwise, X } from "@phosphor-icons/react";
import { toast } from "sonner";

function copy(text, msg) {
  navigator.clipboard?.writeText(text).then(() => toast.success(msg || "Copied")).catch(() => toast.error("Copy failed"));
}

export default function SupportDialog({ open, onOpenChange }) {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState("");
  const [creating, setCreating] = useState(false);
  const [session, setSession] = useState(null); // {machine_id, code, join_url, expires_at}
  const [connected, setConnected] = useState(false);
  const [ending, setEnding] = useState(false);

  useEffect(() => {
    if (!open) {
      setSession(null); setConnected(false); setCustomer(""); setCreating(false); setEnding(false);
    }
  }, [open]);

  const start = async () => {
    setCreating(true);
    try {
      const res = await api.post(`/orgs/${currentOrgId}/support-sessions`, { customer_name: customer.trim() });
      setSession(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start a support session");
    } finally {
      setCreating(false);
    }
  };

  // Poll for the customer's helper coming online.
  const poll = useCallback(async () => {
    if (!session?.machine_id || !currentOrgId) return;
    try {
      const res = await api.get(`/orgs/${currentOrgId}/support-sessions`);
      const s = (res.data || []).find((x) => x.machine_id === session.machine_id);
      if (s?.online) setConnected(true);
    } catch { /* ignore */ }
  }, [session, currentOrgId]);

  useEffect(() => {
    if (!session || connected) return;
    const t = setInterval(poll, 3000);
    return () => clearInterval(t);
  }, [session, connected, poll]);

  const endSession = async () => {
    if (!session) return;
    setEnding(true);
    try {
      await api.post(`/orgs/${currentOrgId}/support-sessions/${session.machine_id}/end`);
      toast.success("Support session ended");
      onOpenChange(false);
    } catch {
      toast.error("Could not end the session");
    } finally {
      setEnding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-lg overflow-hidden" data-testid="support-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Lifebuoy size={20} weight="fill" className="text-blue-400" /> Controlly Snap — instant support
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Invite someone who doesn't have an account. They run a one-time helper — no signup, nothing left installed.
          </DialogDescription>
        </DialogHeader>

        {!session ? (
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-zinc-300">Customer name <span className="text-zinc-600">(optional)</span></Label>
              <Input
                data-testid="support-customer-input"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && start()}
                placeholder="e.g. Jane's laptop"
                maxLength={60}
                className="bg-zinc-950 border-white/10 mt-1.5"
              />
            </div>
            <Button data-testid="support-start-button" onClick={start} disabled={creating} className="w-full bg-blue-600 hover:bg-blue-500">
              {creating ? "Creating…" : "Create Controlly Snap session"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-lg border border-white/10 bg-zinc-950 p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5">
                <LinkSimple size={13} /> Send this link to your customer
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <code data-testid="support-join-url" className="flex-1 min-w-0 break-all text-xs text-blue-300 font-mono leading-relaxed">{session.join_url}</code>
                <Button size="sm" variant="outline" data-testid="support-copy-link" className="border-white/10 hover:bg-zinc-800 shrink-0"
                  onClick={() => copy(session.join_url, "Support link copied")}>
                  <Copy size={15} className="mr-1.5" /> Copy
                </Button>
              </div>
            </div>

            <div className="rounded-lg border border-white/10 bg-zinc-950 p-4">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5">Or read them this backup code</div>
              <div className="flex items-center justify-between">
                <div data-testid="support-code" className="text-3xl font-mono font-bold tracking-[0.3em] text-white">{session.code}</div>
                <Button size="sm" variant="outline" className="border-white/10 hover:bg-zinc-800"
                  onClick={() => copy(session.code, "Code copied")}>
                  <Copy size={15} className="mr-1.5" /> Copy
                </Button>
              </div>
              <p className="text-xs text-zinc-500 mt-2">They enter it at your site's <span className="font-mono text-zinc-400">/support</span> page. Link or code expires in ~10 min if unused.</p>
            </div>

            {connected ? (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4 flex items-center justify-between" data-testid="support-connected">
                <div className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
                  <CheckCircle size={18} weight="fill" /> Customer connected!
                </div>
                <Button size="sm" data-testid="support-open-session" className="bg-emerald-600 hover:bg-emerald-500"
                  onClick={() => { onOpenChange(false); navigate(`/session/${session.machine_id}`); }}>
                  <Plugs size={16} weight="bold" className="mr-1.5" /> Open session
                </Button>
              </div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-zinc-950 p-4 flex items-center gap-2 text-sm text-zinc-400" data-testid="support-waiting">
                <ArrowClockwise size={16} className="animate-spin text-blue-400" />
                Waiting for your customer to run the helper…
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button variant="ghost" size="sm" onClick={endSession} disabled={ending}
                data-testid="support-end-button" className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                <X size={15} className="mr-1.5" /> {ending ? "Ending…" : "End session"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
